import { format, fromUnixTime } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { MatchParams } from ".";
import { BookingDetails } from "../../../components/Brand/BookingDetails";
import { Card } from "../../../components/Card";
import { Flex } from "../../../components/Flex";
import { Modal } from "../../../components/Modal";
import { Column, Table } from "../../../components/Table";
import { Text } from "../../../components/Text";
import { View } from "../../../components/View";
import { DEFAULT_TIMEZONE } from "../../../config";
import { BrandBookingsQuery } from "../../../graphql/generated";
import styled from "../../../styles";

interface Props {
  bookings: BrandBookingsQuery["bookings"]["bookings"];
  lastElementRef: (node: any) => void;
  isFetching: boolean;
}

const Wrap = styled.div`
  height: 100%;
  padding: 0 0 ${(p) => p.theme.spacing.xl};
`;

export const CompletedBookings = ({
  bookings,
  lastElementRef,
  isFetching,
}: Props) => {
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null);
  let idMatch = useRouteMatch<MatchParams>("/b/bookings/:page/:id");
  const history = useHistory();

  const getBookingTimezone = useCallback(
    (booking: BrandBookingsQuery["bookings"]["bookings"][0]) => {
      return booking.location?.timezone || DEFAULT_TIMEZONE;
    },
    []
  );

  useEffect(() => {
    setActiveBookingId(idMatch && idMatch.params.id ? idMatch.params.id : null);
  }, [idMatch]);

  const columns: Column<BrandBookingsQuery["bookings"]["bookings"][0]>[] = [
    {
      header: "Name",
      key: "name",
      width: 160,
      render: (booking) => (
        <Text margin="0" colorPreset="text" isCompact truncate>
          {booking.creator.preferredProfile?.name}
        </Text>
      ),
    },
    {
      header: "Handle",
      key: "handle",
      width: 130,
      render: (booking) => (
        <Text margin="0" colorPreset="text" truncate>
          {booking.creator.preferredProfile?.username}
        </Text>
      ),
    },
    {
      header: "Listing",
      key: "listing",
      width: 160,
      render: (booking) => (
        <Text margin="0" colorPreset="text" size="s" truncate>
          {booking.listing.name}
        </Text>
      ),
    },
    {
      header: "Location",
      key: "location",
      width: 130,
      render: (booking) => (
        <Text margin="0" colorPreset="text" size="s" truncate>
          {booking.location ? booking.location.name : ""}
        </Text>
      ),
    },
    {
      header: "Date of Visit",
      key: "visitDate",
      width: 130,
      render: (booking) => {
        const timezone = getBookingTimezone(booking);
        return (
          <Text margin="0" colorPreset="text">
            {booking.confirmedTimeslot
              ? formatInTimeZone(
                  fromUnixTime(booking.confirmedTimeslot.date),
                  timezone,
                  "dd/MM/yyyy"
                )
              : booking.redeemedAt
              ? formatInTimeZone(
                  fromUnixTime(booking.redeemedAt),
                  timezone,
                  "dd/MM/yyyy"
                )
              : ""}
          </Text>
        );
      },
    },
    {
      header: "Date of Completion",
      key: "completionDate",
      width: 160,
      render: (booking) => (
        <Text margin="0" colorPreset="text">
          {booking.completedAt
            ? format(fromUnixTime(booking.completedAt), "dd/MM/yyyy")
            : booking.updatedAt
            ? format(fromUnixTime(booking.updatedAt), "dd/MM/yyyy")
            : ""}
        </Text>
      ),
    },
    {
      header: "Feedback Score",
      key: "rating",
      width: 130,
      render: (booking) => (
        <Text margin="0" colorPreset="text">
          {booking.rating ? booking.rating : ""}
        </Text>
      ),
    },
    {
      header: "Feedback details",
      key: "feedback",
      width: 160,
      render: (booking) => (
        <Text margin="0" colorPreset="text" truncate>
          {booking.feedback ? booking.feedback : ""}
        </Text>
      ),
    },
  ];

  // Only show the notification column if at least one booking has showReviewNotification
  const hasReviewNotifications = bookings.some(
    (booking) => booking.showReviewNotification
  );

  if (hasReviewNotifications) {
    columns.unshift({
      header: "",
      key: "bookingRating",
      width: 20,
      noPadding: true,
      render: (booking) => {
        if (booking.showReviewNotification) {
          return (
            <Flex align="center" justify="center" style={{ marginRight: -20 }}>
              <NotificationIndicator isActive={true} />
            </Flex>
          );
        }

        return null;
      },
    });
  }

  return (
    <Wrap>
      <Modal
        isOpen={activeBookingId ? true : false}
        setIsOpen={() => setActiveBookingId(null)}
        onClose={() => {
          history.replace("/b/bookings/completed");
          setActiveBookingId(null);
        }}
        maxWidth={960}
        zIndex={950} // less than 999 to allow for modal stack
        noPadding
      >
        <BookingDetails
          placement="modal"
          bookingId={activeBookingId ? activeBookingId : ""}
        />
      </Modal>

      <View padding="0 0 xl">
        <Card>
          <Table
            columns={columns}
            data={bookings}
            onRowClick={(booking) => {
              history.push(`/b/bookings/completed/${booking.id}`);
            }}
            lastElementRef={lastElementRef}
            isFetching={isFetching}
          />
        </Card>
      </View>
    </Wrap>
  );
};

const NotificationIndicator = styled.span<{ isActive: boolean }>`
  height: 5px;
  width: 5px;
  border-radius: 100%;
  background-color: ${(p) => p.theme.color.primary};
  opacity: ${(p) => (p.isActive ? 1 : 0)};
  transition: opacity 150ms ease-out;
`;
